import React from "react";
import PolicyDocument from "./PolicyDocument";

const policies = {
  orders: {
    slug: "orders",
    path: "/orders",
    eyebrow: "Before and after checkout",
    title: "Order & Cancellation Policy",
    lead: "How sickzuno reviews orders, handles change requests and responds when an order cannot be fulfilled.",
    notice: {
      title: "Act quickly:",
      text: "email us as soon as possible if an order needs to be changed or cancelled. Dispatch can make a change impossible.",
    },
    sections: [
      {
        title: "Order acknowledgement",
        paragraphs: [
          "An on-screen or email acknowledgement confirms that an order request was received; it does not guarantee acceptance. An order is accepted when payment is approved and sickzuno confirms that it can be fulfilled.",
        ],
      },
      {
        title: "Order review",
        paragraphs: [
          "We may review an order for inventory, pricing, address, payment or fraud-prevention concerns. We may contact the email supplied at checkout before accepting or dispatching it.",
        ],
      },
      {
        title: "Customer changes and cancellations",
        items: [
          "Send the order reference and requested change to harv@sickzuno.online.",
          "A delivery address, item or quantity may be changed only before packing or dispatch.",
          "If cancellation is still possible, any captured payment will be returned to the original payment method.",
          "After dispatch, the Returns & Replacements Policy applies instead.",
        ],
      },
      {
        title: "When sickzuno may cancel",
        paragraphs: [
          "We may decline or cancel an order if an item is unavailable, information is incomplete, payment cannot be authorized, a price or description contains a material error, delivery is not reasonably available, or the order appears unlawful, abusive or fraudulent.",
          "If we cancel after payment has been captured, we will initiate a refund for the cancelled amount. This does not affect rights that cannot be excluded under applicable law.",
        ],
      },
      {
        title: "Out-of-stock and partial orders",
        paragraphs: [
          "If an item becomes unavailable, customer care may offer a delay, substitution or cancellation. We will not substitute an item without the customer’s agreement. Any split-shipment arrangement will be explained before an additional shipping charge is applied.",
        ],
      },
      {
        title: "Order records",
        paragraphs: [
          "Keep the order reference, payment receipt and delivery messages until the order is complete. Contact us promptly if an acknowledgement contains an error.",
        ],
      },
    ],
    related: [
      { label: "Payment & Billing", to: "/payments" },
      { label: "Shipping & Delivery", to: "/shipping" },
      { label: "Returns & Replacements", to: "/returns" },
    ],
  },
  payments: {
    slug: "payments",
    path: "/payments",
    eyebrow: "Checkout information",
    title: "Payment & Billing Policy",
    lead: "What happens when a customer proceeds from the sickzuno checkout to the hosted payment page.",
    callout: {
      title: "Card details stay on the hosted payment page",
      text: "The sickzuno checkout form does not ask for or store a full card number or card security code.",
    },
    sections: [
      {
        title: "Currency and totals",
        paragraphs: [
          "Store prices and checkout totals are displayed in U.S. dollars unless the page expressly says otherwise. The order summary shows item charges, discounts and the shipping amount before the customer continues to payment.",
        ],
      },
      {
        title: "Hosted payment processing",
        paragraphs: [
          "Selecting Pay now sends the order reference, amount, currency, checkout email and first product title to the site payment endpoint. The customer is then redirected to a third-party hosted payment page. That provider processes card information under its own terms and privacy notice.",
        ],
      },
      {
        title: "Authorization and capture",
        paragraphs: [
          "A payment may be declined or require additional verification by the card issuer or payment provider. An authorization or pending entry is not always a completed charge. sickzuno cannot override an issuer’s decision.",
        ],
      },
      {
        title: "Billing accuracy",
        items: [
          "Use a payment method you are authorized to use.",
          "Provide accurate contact and billing information on the hosted payment page.",
          "Do not email card numbers, security codes or account passwords to customer care.",
          "Contact us promptly about an unfamiliar sickzuno order before starting a charge dispute, when safe to do so.",
        ],
      },
      {
        title: "Payment errors and duplicate charges",
        paragraphs: [
          "If checkout is interrupted, check the payment provider’s result and your order confirmation before trying again. Send screenshots only after removing card and bank details. Verified duplicate captures will be reviewed for refund to the original payment method.",
        ],
      },
      {
        title: "Refunds",
        paragraphs: [
          "Approved refunds are sent to the original payment method where possible. The payment provider or financial institution controls how long a credit takes to appear. Refund eligibility is governed by the Returns & Replacements Policy and applicable consumer law.",
        ],
      },
    ],
    related: [
      { label: "Secure Shopping", to: "/security" },
      { label: "Orders & Cancellations", to: "/orders" },
      { label: "Privacy Policy", to: "/privacy" },
    ],
  },
  pricing: {
    slug: "pricing",
    path: "/pricing",
    eyebrow: "Clear purchase information",
    title: "Pricing, Promotions & Gift Cards Policy",
    lead: "How displayed prices, promotional codes, discounts and any offered gift cards are handled.",
    sections: [
      {
        title: "Displayed prices",
        paragraphs: [
          "Prices are shown in U.S. dollars unless another currency is identified. A product page price can vary by package, weight or option. The checkout total controls for the specific order, subject to correction of clear errors before acceptance.",
        ],
      },
      {
        title: "Taxes and delivery charges",
        paragraphs: [
          "Shipping is displayed separately in the cart or checkout. Taxes, duties and import charges may depend on the destination and may be added or collected separately where required.",
        ],
      },
      {
        title: "Promotional codes",
        items: [
          "Codes must be entered before payment and cannot normally be added after an order is placed.",
          "A code may be limited by date, product, minimum spend, customer, destination or number of uses.",
          "Unless the offer says otherwise, one promotional code may be used per order and codes have no cash value.",
          "A discount may be withdrawn if it was obtained through manipulation, duplication or other misuse.",
        ],
      },
      {
        title: "Price changes and errors",
        paragraphs: [
          "Prices and offers may change prospectively. If a material pricing error affects an unaccepted order, we may ask whether the customer wants to continue at the corrected price or cancel without charge. Statutory pricing rights remain unaffected.",
        ],
      },
      {
        title: "Gift cards and store credit",
        paragraphs: [
          "If gift cards or store credits are offered, their value, expiry rules and territory restrictions will be disclosed at purchase or issue. They are not redeemable for cash except where law requires and must not be used for unauthorized resale or payment fraud.",
        ],
      },
      {
        title: "Returns from discounted orders",
        paragraphs: [
          "A refund for an eligible discounted item is based on the amount actually paid for that item. If a return causes an order to stop meeting a promotion’s conditions, any adjustment will be explained and applied only where the offer terms and applicable law allow it.",
        ],
      },
    ],
    related: [
      { label: "Payment & Billing", to: "/payments" },
      { label: "International Orders", to: "/international" },
      { label: "Terms & Conditions", to: "/terms" },
    ],
  },
  international: {
    slug: "international",
    path: "/international",
    eyebrow: "Cross-border shopping",
    title: "International Orders, Duties & Taxes Policy",
    lead: "Important information for orders delivered outside the seller’s dispatch location.",
    notice: {
      title: "Destination rules vary:",
      text: "customers are responsible for checking whether food ingredients may be imported into the delivery country.",
    },
    sections: [
      {
        title: "Available destinations",
        paragraphs: [
          "The country selector at checkout shows destinations currently offered by the storefront. Availability can change because of carrier service, sanctions, customs rules or product restrictions.",
        ],
      },
      {
        title: "Duties, taxes and fees",
        paragraphs: [
          "Unless checkout expressly states that import charges are included, the recipient may need to pay customs duties, import taxes, brokerage or carrier handling fees. These charges are set by third parties and are not part of the sickzuno checkout total.",
        ],
      },
      {
        title: "Customs information",
        paragraphs: [
          "Customers must provide complete and accurate recipient information. We may provide order and product information to carriers or customs authorities when needed to transport or clear a shipment.",
        ],
      },
      {
        title: "Food and agricultural restrictions",
        items: [
          "Some countries restrict seeds, dried plants, teas, mushrooms, animal-derived ingredients or untreated agricultural goods.",
          "A carrier or customs authority may inspect, delay, return or destroy a prohibited shipment.",
          "Contact customer care before ordering if local import eligibility is uncertain.",
        ],
      },
      {
        title: "Delays, refusal and return to sender",
        paragraphs: [
          "Customs delays are outside sickzuno’s direct control. If a parcel is refused or returned because charges were unpaid or import requirements were not met, any refund will be assessed after the parcel returns and may exclude non-recoverable shipping or official charges where permitted by law.",
        ],
      },
      {
        title: "Currency conversion",
        paragraphs: [
          "A bank or card issuer may convert the U.S.-dollar charge and apply its own exchange rate or foreign transaction fee. Questions about those charges should be directed to the financial institution.",
        ],
      },
    ],
    related: [
      { label: "Shipping & Delivery", to: "/shipping" },
      { label: "Product Safety & Allergens", to: "/product-safety" },
      { label: "Pricing & Promotions", to: "/pricing" },
    ],
  },
  privacyChoices: {
    slug: "privacy-choices",
    path: "/privacy-choices",
    eyebrow: "Access and control",
    title: "Your Privacy Choices",
    lead: "How to ask about personal information and exercise rights that apply where you live.",
    callout: {
      title: "No sale or targeted-ad sharing in the current storefront",
      text: "The application code does not sell personal information or use it for cross-context behavioral advertising.",
    },
    sections: [
      {
        title: "Available requests",
        paragraphs: [
          "Depending on the law where you live and subject to its exceptions, you may ask to know or access personal information, correct inaccurate information, delete information, receive a portable copy, restrict or object to certain processing, withdraw consent, or appeal a denied request.",
        ],
      },
      {
        title: "Browser data you control directly",
        paragraphs: [
          "Cart contents are stored in local browser storage. You can delete them by emptying the cart or clearing this site’s data in browser settings. The recent order summary is stored for the browser session and can be removed by closing the session or clearing site data.",
        ],
      },
      {
        title: "Submitting a request",
        items: [
          "Email harv@sickzuno.online with the subject “Privacy Request.”",
          "Describe the right you want to exercise and the email or order reference connected to the request.",
          "Do not send a password, full card number, government identification number or unnecessary sensitive information in the first message.",
        ],
      },
      {
        title: "Verification",
        paragraphs: [
          "We may ask for information reasonably needed to match a request to an order or prior interaction. If identity cannot be verified, the response may be limited to protect another person’s data. Authorized agents may be asked for proof of authority where applicable.",
        ],
      },
      {
        title: "Sale, sharing and targeted advertising",
        paragraphs: [
          "The current storefront code does not sell personal information, share it for cross-context behavioral advertising, or run application-specific advertising trackers. If those practices change, sickzuno will update its notices and provide any legally required opt-out method before using information that way.",
        ],
      },
      {
        title: "Response and appeals",
        paragraphs: [
          "We will respond within the period required by applicable law and explain if more time or an exception is needed. A customer may reply to request review of a denied privacy request. Exercising a privacy right will not result in unlawful discrimination.",
        ],
      },
    ],
    related: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Cookie Policy", to: "/cookies" },
      { label: "Secure Shopping", to: "/security" },
    ],
  },
  security: {
    slug: "security",
    path: "/security",
    eyebrow: "Safer checkout habits",
    title: "Secure Shopping & Fraud Prevention Policy",
    lead: "The safeguards, customer responsibilities and reporting steps that support safer shopping.",
    notice: {
      title: "sickzuno will never ask by email",
      text: "for a full payment-card number, card security code, online-banking password or one-time authentication code.",
    },
    sections: [
      {
        title: "Hosted card entry",
        paragraphs: [
          "Card details are entered on the third-party hosted payment page, not in the sickzuno checkout form. The payment provider and card networks apply their own technical and fraud-prevention controls.",
        ],
      },
      {
        title: "Order screening",
        paragraphs: [
          "An order may be delayed, declined or cancelled when payment, address or activity signals indicate a material risk of fraud, unauthorized use or abuse. We may request limited additional confirmation but will not request sensitive authentication secrets by email.",
        ],
      },
      {
        title: "Customer precautions",
        items: [
          "Check the site address before entering checkout information.",
          "Use a current browser and a device you trust.",
          "Do not share payment links, one-time codes or account credentials.",
          "Avoid sending card or identity documents through the contact form.",
          "Keep order references private except when working with customer care, the carrier or the payment provider.",
        ],
      },
      {
        title: "Suspicious messages and websites",
        paragraphs: [
          "Do not open an unexpected attachment or pay through a link claiming to be sickzuno if its source is uncertain. Forward the details, without sensitive data, to harv@sickzuno.online so the message can be reviewed.",
        ],
      },
      {
        title: "Reporting an issue",
        paragraphs: [
          "For a suspected unauthorized charge, contact the payment provider or card issuer promptly, then email sickzuno with the order reference and non-sensitive details. For a suspected site vulnerability, provide a clear description and allow reasonable time for review before public disclosure.",
        ],
      },
      {
        title: "No absolute guarantee",
        paragraphs: [
          "No online service can promise perfect security. sickzuno reviews reasonable reports and will provide notices required by applicable law if a qualifying incident occurs.",
        ],
      },
    ],
    related: [
      { label: "Payment & Billing", to: "/payments" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Acceptable Use", to: "/acceptable-use" },
    ],
  },
  accessibility: {
    slug: "accessibility",
    path: "/accessibility",
    eyebrow: "Shopping for more people",
    title: "Accessibility Statement",
    lead: "sickzuno aims to make product information, navigation and checkout understandable and usable for people with disabilities.",
    callout: {
      title: "Our goal",
      text: "We use WCAG 2.2 Level AA as a reference point for ongoing accessibility work; this statement is a commitment to improvement, not a claim of audited conformance.",
    },
    sections: [
      {
        title: "Measures we take",
        items: [
          "Use semantic headings, labels and landmarks to support navigation.",
          "Provide keyboard-operable links, forms and shopping controls.",
          "Use visible focus styles, readable contrast and responsive layouts.",
          "Provide text alternatives for meaningful product imagery where available.",
          "Respect reduced-motion preferences for non-essential animation where implemented.",
        ],
      },
      {
        title: "Technology",
        paragraphs: [
          "The storefront relies on modern HTML, CSS and JavaScript. Core pages are designed for current major browsers and common assistive technologies, but combinations of older software may behave differently.",
        ],
      },
      {
        title: "Known limitations",
        paragraphs: [
          "Third-party hosted payment pages are controlled by their provider. Some product imagery or older content may not yet convey every visual detail in text. We welcome reports that identify a page, control or task that is difficult to use.",
        ],
      },
      {
        title: "Requesting assistance",
        paragraphs: [
          "Email harv@sickzuno.online with the page address, the task you were trying to complete and the browser or assistive technology used, if you are comfortable sharing it. We can provide product or policy information in a reasonable alternative format where practicable.",
        ],
      },
      {
        title: "Feedback and improvement",
        paragraphs: [
          "Accessibility is reviewed as the storefront changes. Reports are prioritized according to their impact on completing important tasks such as finding information, adding an item to the cart, checking out and contacting support.",
        ],
      },
    ],
    related: [
      { label: "Contact Us", to: "/contact" },
      { label: "Terms & Conditions", to: "/terms" },
      { label: "Privacy Policy", to: "/privacy" },
    ],
  },
  productSafety: {
    slug: "product-safety",
    path: "/product-safety",
    eyebrow: "Ingredients and informed choices",
    title: "Product Safety, Allergens & Use Policy",
    lead: "Guidance for selecting, storing and using spices, herbs, teas and other culinary products safely.",
    notice: {
      title: "Read the product label:",
      text: "the label supplied with the item controls over general website descriptions for ingredients, allergens, preparation and storage.",
    },
    sections: [
      {
        title: "Culinary use only",
        paragraphs: [
          "Products sold as culinary ingredients are intended for food or beverage preparation according to their labels. They are not sold for inhalation, injection, vaping, cosmetic use or treatment of a medical condition unless a product page expressly and lawfully states otherwise.",
        ],
      },
      {
        title: "Allergens and cross-contact",
        paragraphs: [
          "Review the current package before every use because suppliers and formulations can change. People with allergies, celiac disease or other dietary restrictions should contact customer care before purchase if a listing does not provide enough information. Unless expressly certified, do not assume a product or facility is free from a particular allergen or cross-contact risk.",
        ],
      },
      {
        title: "Accuracy of product information",
        paragraphs: [
          "We aim to keep ingredient, origin, weight and dietary information accurate, but packaging received may contain newer information than the website. Contact us before using a product if the listing and package conflict.",
        ],
      },
      {
        title: "Storage and handling",
        items: [
          "Keep products sealed and follow label directions for temperature, moisture and light.",
          "Use clean, dry utensils and avoid returning used product to the package.",
          "Do not use a product with a broken safety seal, unusual odor, visible contamination or unexplained damage.",
          "Keep concentrated ingredients and small items away from children and pets.",
        ],
      },
      {
        title: "Health information",
        paragraphs: [
          "Recipe, traditional-use and ingredient information is educational and is not medical or dietary advice. Ask a qualified health professional about allergies, pregnancy, medication interactions or a medical condition.",
        ],
      },
      {
        title: "Reporting a concern",
        paragraphs: [
          "Stop using a product if a safety concern is suspected. Keep the product, packaging, lot information and order reference, and email harv@sickzuno.online. Seek urgent medical help when symptoms require it; customer care is not an emergency service.",
        ],
      },
    ],
    related: [
      { label: "Product Recalls", to: "/recalls" },
      { label: "Returns & Replacements", to: "/returns" },
      { label: "Satisfaction Guarantee", to: "/guarantee" },
    ],
  },
  recalls: {
    slug: "recalls",
    path: "/recalls",
    eyebrow: "Product safety notices",
    title: "Product Recall & Safety Notice Policy",
    lead: "How sickzuno handles credible recall information and customer reports about a potentially unsafe product.",
    sections: [
      {
        title: "Monitoring and assessment",
        paragraphs: [
          "We review recall or withdrawal notices received from suppliers, manufacturers, carriers and relevant authorities. A report is assessed using the product name, package size, lot or batch code, best-before date and order history when available.",
        ],
      },
      {
        title: "Customer notification",
        paragraphs: [
          "When a notice affects a product sold by sickzuno and contact information is available, we may use the checkout email to send safety instructions. A notice may also be posted on this site. The method depends on the seriousness and information available.",
        ],
      },
      {
        title: "What customers should do",
        items: [
          "Stop using the identified product and separate it from other food.",
          "Follow the notice’s directions; do not discard the label or lot code until instructed.",
          "Do not consume a product merely to test whether it is affected.",
          "Seek appropriate medical assistance for a suspected adverse reaction.",
        ],
      },
      {
        title: "Refund or replacement",
        paragraphs: [
          "Instructions will explain whether the affected product should be returned, discarded or otherwise handled. A verified recall may qualify for refund or replacement without the usual unopened-product condition, subject to the notice and applicable law.",
        ],
      },
      {
        title: "Reporting an unlisted concern",
        paragraphs: [
          "Email harv@sickzuno.online with photographs of the package and label, the lot or batch code, best-before date, order reference and a description of the issue. Do not send medical records or other unnecessary sensitive information.",
        ],
      },
    ],
    related: [
      { label: "Product Safety & Allergens", to: "/product-safety" },
      { label: "Returns & Replacements", to: "/returns" },
      { label: "Privacy Policy", to: "/privacy" },
    ],
  },
  guarantee: {
    slug: "guarantee",
    path: "/guarantee",
    eyebrow: "Customer care promise",
    title: "Satisfaction Guarantee",
    lead: "The practical steps sickzuno takes when an order is damaged, incorrect or materially different from its description.",
    sections: [
      {
        title: "What the guarantee covers",
        paragraphs: [
          "Contact us if an item arrives damaged, is not the item ordered, is missing from the parcel, or materially fails to match the description or condition reasonably expected at delivery.",
        ],
      },
      {
        title: "How to make a claim",
        items: [
          "Email harv@sickzuno.online within 30 days of delivery when reasonably possible.",
          "Include the order reference, affected item and a clear description of the issue.",
          "Keep the packaging, label and product while the claim is reviewed.",
          "Provide photographs when they can safely show damage, packing error or label information.",
        ],
      },
      {
        title: "Available resolutions",
        paragraphs: [
          "Depending on the facts and applicable law, customer care may offer guidance, a replacement, an appropriate partial refund or a full refund of the affected item. We may ask that an item be returned or safely discarded.",
        ],
      },
      {
        title: "What is not normally covered",
        paragraphs: [
          "The guarantee does not normally cover preference changes after opening, normal variation in natural ingredients, damage caused by improper storage after delivery, use contrary to the label, or an issue caused by an inaccurate delivery address supplied by the customer.",
        ],
      },
      {
        title: "Legal rights preserved",
        paragraphs: [
          "This voluntary guarantee is additional to rights and remedies that cannot be limited under applicable consumer law. It does not replace the Returns & Replacements Policy for ordinary change-of-mind requests.",
        ],
      },
    ],
    related: [
      { label: "Returns & Replacements", to: "/returns" },
      { label: "Product Safety & Allergens", to: "/product-safety" },
      { label: "Complaints & Disputes", to: "/complaints" },
    ],
  },
  acceptableUse: {
    slug: "acceptable-use",
    path: "/acceptable-use",
    eyebrow: "Fair and lawful access",
    title: "Acceptable Use Policy",
    lead: "Rules that protect customers, the storefront and its service providers from misuse.",
    sections: [
      {
        title: "Lawful shopping use",
        paragraphs: [
          "Use the storefront only for lawful browsing, purchasing and customer-service purposes. You must provide information you are authorized to provide and use a payment method you are authorized to use.",
        ],
      },
      {
        title: "Prohibited technical activity",
        items: [
          "Attempting unauthorized access to systems, accounts, source data or payment services.",
          "Introducing malware, harmful code or excessive automated traffic.",
          "Bypassing rate limits, security controls, purchasing limits or access restrictions.",
          "Scraping or extracting content at a scale that disrupts the service or violates rights.",
          "Testing vulnerabilities without prior written permission.",
        ],
      },
      {
        title: "Prohibited commercial and harmful activity",
        items: [
          "Fraud, chargeback abuse, promotion abuse or unauthorized resale of gift cards.",
          "Impersonation, false reviews, deceptive messages or misuse of another person’s information.",
          "Content that is unlawful, threatening, discriminatory, defamatory or infringing.",
          "Using sickzuno branding to suggest an unauthorized affiliation or endorsement.",
        ],
      },
      {
        title: "Enforcement",
        paragraphs: [
          "We may limit access, decline an order, preserve relevant records or report conduct when reasonably necessary to protect customers, enforce these terms or comply with law. Measures will be proportionate to the apparent risk where practicable.",
        ],
      },
      {
        title: "Reporting misuse",
        paragraphs: [
          "Send a description, relevant page address and non-sensitive evidence to harv@sickzuno.online. Do not use a report as permission to continue testing or accessing data.",
        ],
      },
    ],
    related: [
      { label: "Terms & Conditions", to: "/terms" },
      { label: "Secure Shopping", to: "/security" },
      { label: "Intellectual Property & Legal", to: "/legal" },
    ],
  },
  legal: {
    slug: "legal",
    path: "/legal",
    eyebrow: "Ownership and lawful use",
    title: "Intellectual Property & Legal Notice",
    lead: "How sickzuno branding, original site materials and third-party rights may be used.",
    notice: {
      title: "Respect for third-party rights:",
      text: "sickzuno does not claim ownership of third-party names, marks or materials merely because they are mentioned for identification or compatibility.",
    },
    sections: [
      {
        title: "sickzuno materials",
        paragraphs: [
          "Unless a notice says otherwise, sickzuno owns or is licensed to use its brand name, logo, original text, layout, graphics and other storefront materials. Applicable copyright, trademark and related laws protect those materials.",
        ],
      },
      {
        title: "Limited personal use",
        paragraphs: [
          "Customers may access and use the storefront for personal, lawful shopping. No permission is granted to reproduce, republish, sell, frame, distribute or create a misleading derivative of protected materials, except as law permits or sickzuno agrees in writing.",
        ],
      },
      {
        title: "Third-party rights",
        paragraphs: [
          "Third-party product names, payment marks and other identifiers belong to their respective owners. Their appearance does not imply sponsorship or endorsement beyond the relationship accurately described on the site.",
        ],
      },
      {
        title: "No deceptive use",
        items: [
          "Do not use sickzuno branding in a domain, advertisement or account that creates confusion.",
          "Do not remove ownership notices or imply that copied material is your own.",
          "Do not use site materials to sell counterfeit, unsafe or unauthorized products.",
          "Do not submit content unless you have the rights needed to share it.",
        ],
      },
      {
        title: "Infringement reports",
        paragraphs: [
          "A rights holder may email harv@sickzuno.online with contact information, identification of the protected work or mark, the exact page or material at issue, the basis for the claim and a statement that the report is accurate. We may request reasonable verification before acting.",
        ],
      },
      {
        title: "Corrections and removal",
        paragraphs: [
          "Credible reports will be reviewed in good faith. Material may be corrected, attributed, restricted or removed while a claim is assessed. Nothing in this notice limits lawful fair use, quotation, criticism or other rights provided by law.",
        ],
      },
    ],
    related: [
      { label: "Acceptable Use", to: "/acceptable-use" },
      { label: "Terms & Conditions", to: "/terms" },
      { label: "Complaints & Disputes", to: "/complaints" },
    ],
  },
  complaints: {
    slug: "complaints",
    path: "/complaints",
    eyebrow: "A clear path to resolution",
    title: "Complaints & Dispute Resolution Policy",
    lead: "How to raise a concern and what information helps sickzuno review it fairly.",
    sections: [
      {
        title: "Contact customer care first",
        paragraphs: [
          "Email harv@sickzuno.online with “Complaint” in the subject. Include the order reference, relevant dates, the product or service involved, what went wrong and the outcome you are requesting.",
        ],
      },
      {
        title: "What not to send",
        paragraphs: [
          "Do not email a full card number, card security code, password, one-time authentication code, medical record or government identification document unless a lawful and secure method has first been arranged and the information is genuinely necessary.",
        ],
      },
      {
        title: "Review process",
        items: [
          "Customer care will acknowledge and organize the available information.",
          "We may request photographs, packaging details, carrier updates or payment-provider information that is reasonably needed.",
          "The applicable order, shipping, return, safety and privacy policies will be considered.",
          "The response will explain the proposed resolution or why the request cannot be granted.",
        ],
      },
      {
        title: "Escalation",
        paragraphs: [
          "If the first response does not resolve the issue, reply with the reason and ask for a second review. New information should be included. A privacy-right denial may be appealed through the same email channel where applicable law provides that right.",
        ],
      },
      {
        title: "External rights",
        paragraphs: [
          "This process does not prevent a customer from using a payment-provider process, contacting a consumer-protection or data-protection authority, bringing an eligible small claim, or using another remedy available under applicable law.",
        ],
      },
      {
        title: "Good-faith communication",
        paragraphs: [
          "Both sides should preserve relevant records and communicate honestly. Fraudulent evidence, threats or abusive conduct may be handled under the Acceptable Use Policy, but a firm or negative complaint will not be rejected merely because it is critical.",
        ],
      },
    ],
    related: [
      { label: "Satisfaction Guarantee", to: "/guarantee" },
      { label: "Returns & Replacements", to: "/returns" },
      { label: "Your Privacy Choices", to: "/privacy-choices" },
    ],
  },
};

const createPolicyComponent = (policy) => function PolicyPage() {
  return <PolicyDocument policy={policy} />;
};

export const OrdersPolicy = createPolicyComponent(policies.orders);
export const PaymentsPolicy = createPolicyComponent(policies.payments);
export const PricingPolicy = createPolicyComponent(policies.pricing);
export const InternationalPolicy = createPolicyComponent(policies.international);
export const PrivacyChoices = createPolicyComponent(policies.privacyChoices);
export const SecurityPolicy = createPolicyComponent(policies.security);
export const AccessibilityPolicy = createPolicyComponent(policies.accessibility);
export const ProductSafetyPolicy = createPolicyComponent(policies.productSafety);
export const RecallPolicy = createPolicyComponent(policies.recalls);
export const GuaranteePolicy = createPolicyComponent(policies.guarantee);
export const AcceptableUsePolicy = createPolicyComponent(policies.acceptableUse);
export const LegalNotice = createPolicyComponent(policies.legal);
export const ComplaintsPolicy = createPolicyComponent(policies.complaints);

